import { FC, useRef, useState } from 'react';
import { useInstance } from '@meraki-internal/react-dependency-injection';
import { useSubscription } from '@meraki-internal/state';
import { useIsNative } from '../../utils/useIsNative';
import { IMedication, IPatient } from '../../caremap/ICareMap';
import { CareMapState } from '../../caremap/CareMapState';
import { MixPanelEventEmitter } from '../../metrics/MixPanelEventEmitter';
import { MedicationDescriptor } from './MedicationDescriptor';
import { MedicationFormState } from './MedicationFormState';
import { AddMedicationButton } from './AddMedicationButton';
import { AddMedicationLauncher } from './AddMedicationLauncher';
import { IStructuredRecordSection, StructuredRecordList } from '../structured-records/StructuredRecordList';

export type IMedicationList = {
    patient: IPatient;
    canAddEdit: boolean;
    medications?: IMedication[];
    onAdd: () => void;
    onClick: (id: string) => void;
};

export const MedicationList: FC<IMedicationList> = ({ medications, canAddEdit, patient, onClick, onAdd }) => {
    const descriptor = useInstance(MedicationDescriptor);
    const launcher = useInstance(AddMedicationLauncher);
    const mixpanel = useInstance(MixPanelEventEmitter);

    const careMapState = useInstance(CareMapState);
    useSubscription(() => careMapState);

    const medicationForm = useInstance(MedicationFormState);
    useSubscription(() => medicationForm);

    const isNative = useIsNative();

    const [processing, setProcessing] = useState(false);

    // holds the dropzone's imperative open() once AddMedicationButton reports it -- a ref (not
    // state) since invoking it later needn't re-render this component.
    const takePhotoRef = useRef<() => void>(() => {});

    const onAddHandler = (initialValues?: IMedication, usedPhoto?: boolean) => {
        if (initialValues) {
            medicationForm.setInitialValues(initialValues, usedPhoto);
        }
        onAdd();
    };

    // Single "+ Add Medication" entry point (StructuredRecordList's heading button) replaces the
    // old always-visible photo button + mobile-only "Add Manually" button. On web there's no
    // camera / native OCR, so it goes straight to the manual editor; on native -- or web with the
    // simulateNative dev setting -- it opens the photo/type choice sheet.
    const handleAddClick = () => {
        if (!isNative) {
            mixpanel.track('Add Medication Initiated', () => ({ 'Type': 'manual' }));
            onAddHandler();
            return;
        }

        launcher.open({
            onTakePhoto: () => {
                mixpanel.track('Add Medication Initiated', () => ({ 'Type': 'photo' }));
                takePhotoRef.current();
            },
            onType: () => {
                mixpanel.track('Add Medication Initiated', () => ({ 'Type': 'manual' }));
                onAddHandler();
            }
        });
        launcher.openChoice();
    };

    const active = (medications || []).filter(m => !m.noLongerTaking);
    const discontinued = (medications || []).filter(m => m.noLongerTaking);

    // Only split into sections once there's a discontinued medication to show -- otherwise this
    // renders identically to a flat list, same as before this group existed.
    const sections: IStructuredRecordSection<IMedication>[] | undefined = discontinued.length > 0 ? [
        // omit the (blank-labelled) active group when every medication is discontinued, so its
        // "No medications" empty-state line doesn't render above the "Not currently taking" rows
        ...(active.length > 0 ? [{ label: '', records: active }] : []),
        { label: 'Not currently taking', records: discontinued, collapsible: true, defaultCollapsed: true }
    ] : undefined;

    return (
        <>
            <StructuredRecordList
                descriptor={descriptor}
                records={medications || []}
                canAddEdit={canAddEdit}
                isPublic={patient.medicationsIsPublic ?? false}
                addButtonBusy={processing}
                onTogglePublic={() => {
                    const willBePublic = !patient.medicationsIsPublic;
                    mixpanel.track('Medication Privacy Toggled', () => ({
                        'State': willBePublic ? 'public' : 'private'
                    }));
                    careMapState.updatePatient({ ...patient, medicationsIsPublic: willBePublic });
                }}
                onRowClick={onClick}
                onAdd={handleAddClick}
                sections={sections}
            />

            {canAddEdit && (
                <AddMedicationButton
                    onAdd={onAddHandler}
                    onProcessingChange={(isProcessing) => {
                        setProcessing(isProcessing);
                        // a received photo starts processing -> the choice has been made, so dismiss
                        // the sheet now (it stayed open behind the native picker until a file landed)
                        if (isProcessing) {
                            launcher.cancel();
                        }
                    }}
                    onPickerCancel={launcher.cancel}
                    onOpenReady={(open) => { takePhotoRef.current = open; }}
                />
            )}
        </>
    );
};
